/*
 * Figma `118:308` — Credential Reveal. State: Hidden, Revealed, Copied, Expired, Revoked.
 *
 * Reveal-once, and the "once" is the whole design. These are LINE channel secrets, platform access
 * tokens and provider keys: the server hands the full value back at most once, so the screen must
 * make revealing a deliberate act, say plainly that it will not be shown again, and then stop
 * holding it.
 *
 * Three things follow from that and none is decoration:
 *
 * - the secret lives in component state only, is never written to a prop, a URL or a log, and is
 *   dropped on unmount and after `HIDE_AFTER_MS` of sitting on screen — a credential left revealed
 *   on a laptop in a production office is the failure this component exists to avoid;
 * - an expired or revoked credential offers no reveal at all. The value may still exist server-side;
 *   showing it would invite someone to paste a key that will be refused, and debug the wrong thing;
 * - "copied" is a state of the button, not a toast. The person is about to paste into another
 *   window and needs the confirmation where their eyes already are.
 *
 * A reveal that returns nothing is the correct answer from an endpoint that will not re-issue a
 * secret, not an error — so it is reported as "ออกใหม่แทนการดูของเดิม" rather than as a failure.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { StatusBadge } from './StatusBadge';
import { IconEye, IconEyeOff, IconCopy, IconWarning, IconSuccess } from './icons';

export type CredentialStatus = 'active' | 'expired' | 'revoked';

const STATUS: Record<CredentialStatus, { tone: 'success' | 'warning' | 'danger'; label: string }> = {
  active: { tone: 'success', label: 'ใช้งานได้' },
  expired: { tone: 'warning', label: 'หมดอายุแล้ว' },
  revoked: { tone: 'danger', label: 'ถูกเพิกถอน' },
};

/** How long a revealed secret is allowed to stay on screen before it hides itself again. */
export const HIDE_AFTER_MS = 60_000;

export interface CredentialRevealProps {
  label: string;
  /** What the screen shows until someone asks — normally the suffix the server already returns. */
  masked: string;
  status?: CredentialStatus;
  /**
   * Called only when someone asks. Returns the full value once, or `null` where the endpoint will
   * not hand a secret back. Omit entirely where the value is never retrievable.
   */
  reveal?: () => Promise<string | null> | string | null;
  /** e.g. "หมดอายุ 7 ธ.ค. 2026" — shown beside the status so expiry is readable before it bites. */
  expiryNote?: string;
  lastUsedNote?: string;
  onRevoke?: () => void;
}

export function CredentialReveal({
  label, masked, status = 'active', reveal, expiryNote, lastUsedNote, onRevoke,
}: CredentialRevealProps) {
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refused, setRefused] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const usable = status === 'active' && reveal !== undefined;

  // The secret does not outlive the component, and does not sit on screen indefinitely.
  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const hide = () => {
    setShown(null);
    setCopied(false);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };

  const onReveal = async () => {
    if (shown) { hide(); return; }
    setBusy(true);
    setRefused(false);
    try {
      const value = await reveal!();
      if (value) {
        setShown(value);
        hideTimer.current = setTimeout(hide, HIDE_AFTER_MS);
      } else {
        setRefused(true);
      }
    } catch {
      setRefused(true);
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!shown) return;
    try {
      await navigator.clipboard?.writeText(shown);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 4000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="jt-credential" data-status={status}>
      <div className="jt-credential-head">
        <span className="jt-credential-label">{label}</span>
        <StatusBadge tone={STATUS[status].tone}>{STATUS[status].label}</StatusBadge>
      </div>

      <div className="jt-credential-value">
        <code className="jt-mono jt-credential-secret">{shown ?? masked}</code>
        {usable && (
          <Button
            size="sm"
            variant="secondary"
            icon={shown ? <IconEyeOff size={15} /> : <IconEye size={15} />}
            disabled={busy}
            onClick={onReveal}
          >
            {shown ? 'ซ่อน' : busy ? 'กำลังเปิด…' : 'แสดงค่าเต็ม'}
          </Button>
        )}
        {shown && (
          <Button
            size="sm"
            variant={copied ? 'primary' : 'ghost'}
            icon={copied ? <IconSuccess size={15} /> : <IconCopy size={15} />}
            onClick={onCopy}
          >
            {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
          </Button>
        )}
      </div>

      {(expiryNote || lastUsedNote) && (
        <p className="jt-credential-meta">
          {[expiryNote, lastUsedNote].filter(Boolean).join(' · ')}
        </p>
      )}

      {shown && (
        <p className="jt-credential-warn" role="status">
          <IconWarning size={14} />
          <span>ค่านี้จะไม่ถูกแสดงอีก — คัดลอกเก็บไว้ตอนนี้ และหน้าจอจะซ่อนให้เองภายใน 1 นาที</span>
        </p>
      )}

      {refused && (
        <p className="jt-credential-warn" role="status">
          <IconWarning size={14} />
          <span>ระบบไม่คืนค่าเต็มของ{label} — ออกใหม่แทนการดูของเดิม</span>
        </p>
      )}

      {status !== 'active' && (
        <p className="jt-credential-meta">
          {status === 'expired'
            ? 'ค่านี้หมดอายุแล้ว จึงดูค่าเต็มไม่ได้ — ออกค่าใหม่เพื่อใช้งานต่อ'
            : 'ค่านี้ถูกเพิกถอนแล้ว จึงดูค่าเต็มไม่ได้ — ระบบที่ยังใช้อยู่จะถูกปฏิเสธ'}
        </p>
      )}

      {onRevoke && status === 'active' && (
        <div className="jt-credential-foot">
          <Button size="sm" variant="destructive" onClick={onRevoke}>เพิกถอน{label}</Button>
        </div>
      )}
    </div>
  );
}
